import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { Button } from '~/eds/src/components/Button/Button';
import { EsignatureType } from '~/enums/EsignatureType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { selectTicketId } from '~/redux/selectors';
import { slices } from '~/redux/slices';

import {
  EsignatureEnvelopeStatusValue,
  EsignatureSignerStatusValue,
} from '../types/esignature';
import { useHandleEsignAuthenticateAction } from './useHandleEsignAuthenticateAction';

export const SignDocument = ({ isSignStage }: { isSignStage: boolean }) => {
  const intl = useIntl();
  const { toast } = useErrorToast();
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const ticketId = useSelector(selectTicketId);
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );
  const [signDocument, { isFetching }] =
    api.endpoints.signDocument.useLazyQuery();
  const { data: envelope } = api.endpoints.getEnvelope.useQueryState(
    {
      ticketId,
    },
    {
      skip: !ticketId || !isSignStage,
    },
  );
  const { data: recipients } = api.endpoints.getRecipients.useQueryState(
    {
      ticketId,
    },
    {
      skip: !ticketId || !isSignStage,
    },
  );
  const { data: esignatureTokenStatus } =
    api.endpoints.getTokenStatus.useQueryState(
      {
        ticketId,
      },
      {
        skip: !ticketId || !isSignStage,
      },
    );
  const {
    handleEsignAuthenticateAction,
    isLoading: isLoadingEsignAuthenticate,
  } = useHandleEsignAuthenticateAction();
  const esignatureProvider = esignatureTokenStatus?.providerType;
  const currentUserPendingSignature = recipients?.signers?.find(
    (recipient) =>
      recipient.userId === currentUser.id &&
      recipient.status?.value === EsignatureSignerStatusValue.Pending,
  );

  const hasValidEnvelope =
    !!envelope &&
    envelope.status.value !== EsignatureEnvelopeStatusValue.Error &&
    envelope.status.value !== EsignatureEnvelopeStatusValue.Voided;

  const showSignDocument =
    isSignStage &&
    isEsignatureV2RevampEnabled &&
    hasValidEnvelope &&
    !!currentUserPendingSignature &&
    Boolean(esignatureProvider);

  if (!showSignDocument) {
    return null;
  }

  const handleSignDocument = async () => {
    const newTab = window.open('', '_blank');
    try {
      const result = await signDocument({
        ticketId,
        recipientId: currentUserPendingSignature.id,
        returnUrl: window.location.href,
        // Button will not be rendered if there is no esignatureProvider, so we know here it will always be defined
        vendor: esignatureProvider as EsignatureType,
      }).unwrap();
      if (result?.redirectUrl && newTab) {
        newTab.location.href = result.redirectUrl;
      }
    } catch (error) {
      if (newTab) {
        newTab.close();
      }

      if (error instanceof Error) {
        toast(error, { autoDismiss: false });
      }
    }
  };

  return (
    <Button
      text={intl.formatMessage({
        id: 'SignDocument.signDocumentButton',
        defaultMessage: 'Sign Document',
        description:
          'Button label that takes users to a new tab to sign a document in an esignature platform.',
      })}
      onClick={() => handleEsignAuthenticateAction(handleSignDocument)}
      variant="primary"
      isLoading={isFetching || isLoadingEsignAuthenticate}
    />
  );
};
